import {
  EmployeeFromGroup,
  ExtendedEmployee,
  ServerSalaryRateSettingType,
  ServerSalarySettingType,
} from '@expane/data'
import {
  areIntervalsOverlapping,
  eachDayOfInterval,
  getOverLappingInterval,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
} from '@expane/date'
import { TFunction } from 'i18next'
import { findById } from '../utils'

export const RATE_OPTIONS = [
  { id: 1, name: 'salary.monthly' },
  { id: 2, name: 'salary.daily' },
]

export const getDoesEmployeeHaveSalarySetting = (
  employee: Pick<ExtendedEmployee, 'salarySettings_aggregate'>,
): boolean => Boolean(employee.salarySettings_aggregate.aggregate?.count)

export const isSalaryRateMonthly = (salaryRateSettings: ServerSalaryRateSettingType | undefined) =>
  salaryRateSettings?.rateType === RATE_OPTIONS[0].id

export const getOverlappingSalaryRateSettingAndPeriod = ({
  fromDate,
  toDate,
  salaryRateSetting,
}: {
  fromDate: Date
  toDate: Date
  salaryRateSetting: ServerSalaryRateSettingType
}) => {
  if (!salaryRateSetting.end) {
    if (
      isWithinInterval(salaryRateSetting.start, {
        start: fromDate,
        end: toDate,
      })
    ) {
      return eachDayOfInterval({ start: salaryRateSetting.start, end: toDate })
    }

    if (isBefore(salaryRateSetting.start, fromDate))
      return eachDayOfInterval({ start: fromDate, end: toDate })
    else return []
  } else {
    if (
      areIntervalsOverlapping(
        {
          start: salaryRateSetting.start,
          end: salaryRateSetting.end,
        },
        { start: fromDate, end: toDate },
      )
    ) {
      return getOverLappingInterval(
        { start: fromDate, end: toDate },
        {
          start: salaryRateSetting.start,
          end: salaryRateSetting.end,
        },
      )
    } else return []
  }
}

export interface Threshold {
  qty: number
  value: number
}

export interface ThresholdInForm {
  qty: string
  value: string
}

export type SalarySettingServiceType = {
  id: number
  serviceId: number
  name: string
  type: number
  value: string
  fallbackType?: number
  fallbackValue: string
  dependsOnRealizedValue: boolean
  thresholds?: ThresholdInForm[]
}

export const RATE_FROM_SERVICES_TYPES = {
  fixedValue: { id: 1, name: 'fixedAmount' },
  percentageOfServicePrice: {
    id: 2,
    name: 'salaryRatesFromServices.percentOfServicePrice',
  },
  roundedPercentageOfServicePrice: {
    id: 3,
    name: 'salaryRatesFromServices.roundedPercentageOfServicePrice',
  },
}

export const RATE_FROM_GROUP_SERVICES_TYPES = {
  fixedValueByClientsCount: {
    id: 4,
    name: 'salaryRatesFromServices.fixedAmountByClientsCount',
  },
  percentOfServicePriceByClientsCount: {
    id: 5,
    name: 'salaryRatesFromServices.percentOfServicePriceByClientsCount',
  },
  roundedPercentageOfServicePriceByClientsCount: {
    id: 6,
    name: 'salaryRatesFromServices.roundedPercentageOfServicePriceByClientsCount',
  },
}

export const isRateFromGroupServicesType = (type: number) =>
  Object.values(RATE_FROM_GROUP_SERVICES_TYPES).some(
    rateFromGroupServicesType => rateFromGroupServicesType.id === type,
  )

export const RATE_FROM_SERVICES_FALLBACK_TYPES = {
  fixedValue: RATE_FROM_SERVICES_TYPES.fixedValue,
  percentageOfServicePrice: RATE_FROM_SERVICES_TYPES.percentageOfServicePrice,
}

export const checkIsSomeOfCurrentSalaryRateSettingsIsMonthly = ({
  fromDate,
  toDate,
  salarySettings,
}: {
  fromDate: Date
  toDate: Date
  salarySettings: ServerSalaryRateSettingType[]
}) => {
  const currentSalaryRateSettings = salarySettings.filter(
    salaryRateSetting =>
      getOverlappingSalaryRateSettingAndPeriod({
        fromDate,
        toDate,
        salaryRateSetting,
      }).length,
  )

  return currentSalaryRateSettings.some(setting => isSalaryRateMonthly(setting))
}

export const checkAreEmployeesFirstSalarySetting = (employees: EmployeeFromGroup[]) =>
  employees.every(employee => employee.salarySettings.length === 0)

export const getRateTypeName = (rateType: number, t: TFunction): string => {
  const rateOption = findById(rateType, RATE_OPTIONS)

  if (rateOption) return t(rateOption.name)
  else return '-'
}

export const getAreEmployeesSalarySettingStartDateAlreadyInUse = ({
  employeeIds,
  salarySettings,
  start,
}: {
  employeeIds: number[]
  salarySettings: ServerSalarySettingType[] | undefined
  start: Date
}) => {
  if (!salarySettings) return false

  const salarySettingsBySelectedEmployees = salarySettings.filter(setting =>
    employeeIds.some(id => id === setting.employeeId),
  )

  return getIsStartDateInSettings({ salarySettings: salarySettingsBySelectedEmployees, start })
}

export const getIsStartDateInSettings = ({
  salarySettings,
  start,
}: {
  salarySettings: ServerSalarySettingType[] | undefined
  start: Date
}) => {
  return salarySettings?.some(setting => isSameDay(start, setting.start))
}

export const getAreEmployeesSalarySettingStartDateAlreadyInFuture = ({
  employeeIds,
  salarySettings,
  start,
}: {
  employeeIds: number[]
  salarySettings: ServerSalarySettingType[] | undefined
  start: Date
}) => {
  if (!salarySettings) return false

  const salarySettingsBySelectedEmployees = salarySettings.filter(setting =>
    employeeIds.some(id => id === setting.employeeId),
  )

  return getIsStartDateInFutureInSettings({
    salarySettings: salarySettingsBySelectedEmployees,
    start,
  })
}

export const getIsStartDateInFutureInSettings = ({
  salarySettings,
  start,
}: {
  salarySettings: ServerSalarySettingType[] | undefined
  start: Date
}) => {
  return salarySettings?.some(setting => isAfter(setting.start, start))
}

export type SalaryGroupServiceSettingsThresholdType = 'percentage' | 'fixed' | 'rounded'

export const getThresholdTypeByGroupRateType = (rateType: number) => {
  if (rateType === RATE_FROM_GROUP_SERVICES_TYPES.fixedValueByClientsCount.id) return 'fixed'
  if (rateType === RATE_FROM_GROUP_SERVICES_TYPES.percentOfServicePriceByClientsCount.id)
    return 'percentage'
  return 'rounded'
}

export const getSalarySettingServiceThresholdRowErrorMessage = (
  qtyErrorType: string | undefined,
  valueErrorType: string | undefined,
  t: TFunction,
): string => {
  if (qtyErrorType === 'required' || valueErrorType === 'required') return t('formError.required')

  if (qtyErrorType === 'validate') return t('salarySetting.qtyMustBeUniqueError')
  if (qtyErrorType === 'min') return t('salarySetting.minNumberOfClientsError')

  if (valueErrorType === 'max') return t('salarySetting.maxPercentageError')

  return ''
}

export const sortSalarySettingServiceThresholdFields = (fields: ThresholdInForm[]) =>
  [...fields].sort((a, b) => {
    if (!a.qty || !b.qty) return 1
    return Number(a.qty) - Number(b.qty)
  })

export const sortAndUpdateSalarySettingServiceThresholdFields = (
  fields: ThresholdInForm[],
  replaceFn: (data: ThresholdInForm[]) => void,
) => {
  const sorted = sortSalarySettingServiceThresholdFields(fields)

  if (sorted.length > 1) replaceFn(sorted)
}

export const transformSalarySettingServiceThresholdsForServer = (
  salarySettingService: SalarySettingServiceType,
) => {
  if (
    (salarySettingService.type === RATE_FROM_GROUP_SERVICES_TYPES.fixedValueByClientsCount.id ||
      salarySettingService.type ===
        RATE_FROM_GROUP_SERVICES_TYPES.percentOfServicePriceByClientsCount.id ||
      salarySettingService.type ===
        RATE_FROM_GROUP_SERVICES_TYPES.roundedPercentageOfServicePriceByClientsCount.id) &&
    salarySettingService.thresholds
  ) {
    return {
      data: salarySettingService.thresholds.map(threshold => ({
        qty: Number(threshold.qty),
        value: Number(threshold.value),
      })),
    }
  }

  return undefined
}

export const getSalarySettingServiceThresholdsExplanationText = (
  type: SalaryGroupServiceSettingsThresholdType,
  t: TFunction,
) => {
  if (type === 'fixed')
    return t('salarySettingServiceThresholdsExplanation.fixedValueByClientsCount')
  if (type === 'percentage')
    return t('salarySettingServiceThresholdsExplanation.percentOfServicePriceByClientsCount')

  return t(
    'salarySettingServiceThresholdsExplanation.roundedPercentageOfServicePriceByClientsCount',
  )
}
